import buildLegacyMatrixDefinition, {
  isLegacyMatrixTable,
} from 'components/Studio/buildLegacyMatrixDefinition';
import { readStudioBlock } from 'components/Studio/canvasMapping';
import cardTypeCheckers from 'types/cardTypeCheckers';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';

// Gadget types whose cards register a CSV via GridCsvExportRegistrar on the
// headless PDF path (Studio's Totals and Records tables among them). Studio's
// date comparison and pace tables register too (see isDrawnLegacyTable).
// Classic legacy matrices don't, so they aren't waited on.
const CSV_GADGET_TYPES: string[] = ['matrix', 'simpleGrid'];

// A Studio date comparison or pace table that draws, and so registers its CSV
// (StudioLegacyMatrix) once its grid is ready. One missing its settings draws
// a placeholder, which never registers.
const isDrawnLegacyTable = (
  content: Pick<
    Studio.ChartContent,
    'visType' | 'metricIds' | 'dimensions' | 'dimensionOptions'
  > & { table?: Studio.TableOptions },
): boolean =>
  isLegacyMatrixTable(content) &&
  !(
    'missing' in
    buildLegacyMatrixDefinition({
      card: {
        visType: 'table',
        metricIds: content.metricIds,
        dimensions: content.dimensions,
        dimensionOptions: content.dimensionOptions,
        table: content.table,
      },
      id: '',
      name: '',
    })
  );

const DEFAULT_TIMEOUT_MS = 20000;
const DEFAULT_POLL_MS = 250;

/**
 * The canvas cards a scheduled export should attach a CSV for. Unnamed gadgets
 * are skipped: the registrar needs a name for the file and never registers them.
 */
export const getExpectedGridExportCards = ({
  cards,
  reportGadgetsLookup,
  dashboardGadgetsLookup,
}: {
  cards: CanvasCard.Card[];
  reportGadgetsLookup: Record<string, ReportGadgets.ReportGadget | undefined>;
  dashboardGadgetsLookup: Record<string, DashboardGadget | undefined>;
}): { cardI: string; name: string }[] =>
  cards.flatMap((card) => {
    // A Composite's tables register under their own element ids
    if (cardTypeCheckers.isStudioComposite(card)) {
      const { composite } = card.content;
      return (composite.rows ?? []).flatMap((row) =>
        row.elements.filter(isDrawnLegacyTable).map((element) => ({
          cardI: element.id,
          name: element.label || composite.name || element.id,
        })),
      );
    }

    const reportGadget = cardTypeCheckers.isReportGadget(card)
      ? reportGadgetsLookup[card.content.reportGadgetId]
      : undefined;
    const dashboardGadget = cardTypeCheckers.isDashboardGadget(card)
      ? dashboardGadgetsLookup[card.content.dashboardGadgetId]
      : undefined;
    const chartDef = (reportGadget ?? dashboardGadget)?.chartDef;
    if (isV5ChartDef(chartDef)) {
      return chartDef.name && CSV_GADGET_TYPES.includes(chartDef.gadgetType)
        ? [{ cardI: card.layout.i, name: chartDef.name }]
        : [];
    }

    // A gadget Studio draws (as ReportCanvasCard decides), as a date
    // comparison or pace table
    const studioCard = dashboardGadget
      ? readStudioBlock({ kind: 'dashboard', gadget: dashboardGadget })
      : reportGadget
        ? readStudioBlock({ kind: 'report', gadget: reportGadget })
        : undefined;
    return studioCard && isDrawnLegacyTable(studioCard)
      ? [{ cardI: card.layout.i, name: studioCard.name || card.layout.i }]
      : [];
  });

/**
 * Waits (bounded) until every expected grid card has registered its CSV export
 * callback, then returns the latest callbacks. A grid only registers once its
 * data has loaded and its grid has mounted, so exporting straight away can
 * silently drop CSVs from a scheduled delivery. Anything still missing at the
 * deadline is logged (surfacing in the renderer's logs) and the export proceeds
 * without it rather than failing the delivery.
 */
const waitForGridExportCallbacks = async ({
  expectedCards,
  getGridExportCallbacks,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  pollMs = DEFAULT_POLL_MS,
}: {
  expectedCards: { cardI: string; name: string }[];
  getGridExportCallbacks: () => GridExportCallback[];
  timeoutMs?: number;
  pollMs?: number;
}): Promise<GridExportCallback[]> => {
  const getMissing = () => {
    const registered = new Set(getGridExportCallbacks().map((cb) => cb.cardI));
    return expectedCards.filter((card) => !registered.has(card.cardI));
  };

  const deadline = Date.now() + timeoutMs;
  let missing = getMissing();
  while (missing.length > 0 && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, pollMs));
    missing = getMissing();
  }

  if (missing.length > 0) {
    console.warn(
      `[scheduled-export] CSV not registered for ${missing.length} grid gadget(s), sending without: ${missing
        .map((card) => card.name)
        .join(', ')}`,
    );
  }

  return getGridExportCallbacks();
};

export default waitForGridExportCallbacks;
